<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import type { KpiOpts } from "../sbi/protocol";
import { formatValue, formatValueCompact } from "../format.ts";
import LucentIcon from "./LucentIcon.vue";
import LucentSpinner from "./LucentSpinner.vue";
import InfoTip from "./InfoTip.vue";
import { usePrintMode } from "../sbi/print-mode.ts";
import { useElementWidth } from "../sbi/embed-width.ts";
import { kpiAvailableWidth } from "./kpi-fit";
import { formatError } from "./errorText.ts";

const props = withDefaults(
  defineProps<{
    label: string;
    value: number | string;
    opts?: KpiOpts;
    loading?: boolean;
    /**
     * Set when the marker bound to this KPI rejected. Renders
     * `opts.error` (or the generic fallback) in place of the value.
     * Mutually exclusive with `loading`.
     */
    errored?: boolean;
    /**
     * Error correlation tag minted at the failure site (server or
     * worker). Appended to the user-facing error banner as
     * `[EX:<id>]` so a user reporting the tag maps to one log line.
     */
    exId?: string;
    /**
     * Field → format registry from `sbi.format(...)`. Used as a
     * fallback when `opts.format` isn't provided and the KPI has been
     * bound to a field via `opts.field`.
     */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string): void;
}>();

const isPrint = usePrintMode();

// Resolution order: per-call `format` opt → registered format for
// `opts.field` → no format (raw value). Authors who want the registry
// to apply MUST set `opts.field` — labels are display strings and not
// reliable identifiers to look up by.
const resolvedFormat = computed(() => {
  const explicit = props.opts?.format;
  if (explicit) return explicit;
  const field = props.opts?.field;
  return field ? props.formats[field] : undefined;
});

// Big numbers (e.g. `$12,345,678,901`) blow out of the KPI tile.
// `useAbbreviated` is flipped on by the auto-fit pass below when the
// scaled font-size would have to drop below `MIN_VALUE_FONT_PX`; when
// set, the value renders via `formatValueCompact` (so the same currency
// or percent format is preserved, just in compact notation).
const MIN_VALUE_FONT_PX = 18;
const useAbbreviated = ref(false);

const display = computed(() =>
  useAbbreviated.value
    ? formatValueCompact(props.value, resolvedFormat.value)
    : formatValue(props.value, resolvedFormat.value),
);

const kpiEl = ref<HTMLElement | null>(null);
const valueEl = ref<HTMLElement | null>(null);
const rowEl = ref<HTMLElement | null>(null);
const iconEl = ref<{ $el?: Element } | null>(null);
const deltaEl = ref<HTMLElement | null>(null);

// Re-fits the rendered value to the tile's content width.
// 1. Clears any inline font-size to measure the value at its CSS base
//    size — `calc(30px * var(--lucent-font-scale, 1))`, or 56px for
//    `kpi--primary`. The browser resolves the calc at the computed-
//    value stage, so `getComputedStyle(val).fontSize` returns the
//    post-multiplication pixel value (e.g. 42 at scale 1.4).
// 2. If the natural content width exceeds the tile's content width,
//    sets `font-size` to `base * available / natural` so the text
//    just fits.
// 3. If that ratio would drop below `MIN_VALUE_FONT_PX`, switches to
//    the abbreviated form and re-runs the same measurement on the new,
//    shorter string. Abbreviated text at min size is the final fallback.
function fit(): void {
  const card = kpiEl.value;
  const val = valueEl.value;
  if (!card || !val) return;

  val.style.fontSize = "";

  // The width the value may use: the row, less a trailing icon and a
  // delta that sits on the same line (see kpi-fit.ts). Falls back to
  // the card's content width before the row has rendered.
  const styles = getComputedStyle(card);
  const padX =
    parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
  const row = rowEl.value;
  const icon = iconEl.value?.$el instanceof HTMLElement ? iconEl.value.$el : null;
  const delta = deltaEl.value;
  const availW = kpiAvailableWidth({
    rowWidth: row?.clientWidth || Math.max(0, card.clientWidth - padX),
    iconWidth: icon?.offsetWidth ?? 0,
    deltaWidth: delta?.offsetWidth ?? 0,
    deltaInline: !!delta && delta.offsetTop <= val.offsetTop + 4,
    gap: 10,
  });
  if (availW === 0) return;

  // `scrollWidth` returns the value's natural content width even when
  // the element is currently overflowing its parent — exactly what we
  // need to compute the shrink ratio.
  const naturalW = val.scrollWidth;
  if (naturalW <= availW) return;

  // Read the post-`calc()` computed font-size as the measurement base.
  // The CSS rule is `calc(30px * var(--lucent-font-scale, 1))` (or 56px
  // for primary), so this picks up the variable's current value —
  // 30 at scale 1, 42 at scale 1.4, whatever an embedder set on the
  // host element. Falling back to the hardcoded base keeps the auto-
  // fit working in environments where `getComputedStyle` returns a
  // non-pixel string (happy-dom in tests resolves calc() differently).
  const computedBase = parseFloat(getComputedStyle(val).fontSize);
  const baseFontPx = Number.isFinite(computedBase) && computedBase > 0
    ? computedBase
    : (props.opts?.primary ? 56 : 30);
  const scaled = (baseFontPx * availW) / naturalW;

  if (scaled >= MIN_VALUE_FONT_PX) {
    val.style.fontSize = `${scaled}px`;
    return;
  }

  if (!useAbbreviated.value) {
    useAbbreviated.value = true;
    nextTick(() => fit());
    return;
  }

  // Even the abbreviated form doesn't fit at min size — clamp and let
  // the (now very short) string overflow gracefully if the tile is
  // truly tiny.
  val.style.fontSize = `${MIN_VALUE_FONT_PX}px`;
}

// Reset abbreviation + inline font-size, then re-fit on next tick once
// the DOM has re-rendered at the base size.
function refit(): void {
  useAbbreviated.value = false;
  if (valueEl.value) valueEl.value.style.fontSize = "";
  nextTick(() => fit());
}

watch(
  [
    () => props.value,
    () => resolvedFormat.value,
    () => props.opts?.primary,
    () => props.loading,
    () => props.errored,
  ],
  () => refit(),
  { immediate: true },
);

// Re-fit when the tile's WIDTH changes. Observing the tile directly
// looped: every refit resets the font to its base size, which changes
// the tile's height, which fired the observer again.
const tileWidth = useElementWidth(kpiEl);
watch(tileWidth, () => refit());

// A KPI counts as "empty" when the worker's scalar projection
// resolved to the empty-string sentinel (zero rows / null cell — see
// briefing.md → "Default scalar projection"). The host shows
// `opts.empty` text in place of the value when authors opt in by
// setting it; otherwise the value renders as-is (a blank tile, which
// is the historical behaviour).
const isEmpty = computed(
  () =>
    !props.loading &&
    !props.errored &&
    (props.value === "" || props.value === null || props.value === undefined),
);

const emptyText = computed(() => props.opts?.empty);
const errorText = computed(() =>
  formatError(props.opts?.error ?? "Couldn't load this metric", props.exId),
);

// First-class delta. When `opts.delta` is set the KPI renders an
// integrated number + arrow + caption block alongside the value, and
// the legacy categorical `opts.trend` is suppressed (the delta carries
// its own arrow inferred from sign). When delta is absent, fall back
// to the old `trend`-only rendering for back-compat.
interface ResolvedDelta {
  display: string;
  trend: "up" | "down" | "flat";
  caption?: string;
}

const resolvedDelta = computed<ResolvedDelta | null>(() => {
  const d = props.opts?.delta;
  if (!d || typeof d !== "object" || !("value" in d)) return null;
  const raw = (d as { value: unknown }).value;
  // Marker forms shouldn't reach the host (the worker resolves them
  // before phase-2 emit), but guard anyway so a stray marker doesn't
  // render as `[object Object]`.
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (raw === "") return null;

  const numeric = typeof raw === "number" ? raw : Number(raw);
  const formatStr =
    (d as { format?: string }).format ?? resolvedFormat.value;
  let formatted = formatValue(raw, formatStr);
  // Prefix a `+` for strictly positive numbers so a delta reads as
  // signed at a glance. Negative numbers already carry their own
  // sign; zero stays bare.
  if (typeof numeric === "number" && Number.isFinite(numeric) && numeric > 0) {
    formatted = `+${formatted}`;
  }

  const explicitTrend = (d as { trend?: "up" | "down" | "flat" }).trend;
  let trend: "up" | "down" | "flat";
  if (explicitTrend) {
    trend = explicitTrend;
  } else if (typeof numeric === "number" && Number.isFinite(numeric)) {
    trend = numeric > 0 ? "up" : numeric < 0 ? "down" : "flat";
  } else {
    trend = "flat";
  }

  return {
    display: formatted,
    trend,
    caption: (d as { caption?: string }).caption,
  };
});

// Sparkline. The worker emits `opts.spark = { values: number[] }`
// after resolution; anything else (a stray marker, undefined) means
// no spark to render.
const sparkValues = computed<number[]>(() => {
  const s = props.opts?.spark;
  if (!s || typeof s !== "object" || !("values" in s)) return [];
  const vs = (s as { values: unknown }).values;
  if (!Array.isArray(vs)) return [];
  return vs.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
});

// Build an SVG polyline path normalised to the spark viewBox. The
// viewBox is unitless (0..100 wide × 0..30 tall); the wrapper sizes
// it via CSS. A flat series (min === max) draws a horizontal mid-line
// rather than collapsing to a divide-by-zero NaN.
const SPARK_W = 100;
const SPARK_H = 30;
const sparkPath = computed(() => {
  const vs = sparkValues.value;
  if (vs.length < 2) return "";
  const min = Math.min(...vs);
  const max = Math.max(...vs);
  const range = max - min || 1;
  const stepX = SPARK_W / (vs.length - 1);
  return vs
    .map((v, i) => {
      const x = i * stepX;
      const y = SPARK_H - ((v - min) / range) * SPARK_H;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
});

// Tint the sparkline to match the delta direction when both are set
// — a green up-line under a green +12 reads as one composed signal.
// Without a delta, the spark stays neutral (just colour-of-text).
const sparkClass = computed(() => {
  const t = resolvedDelta.value?.trend;
  if (t === "up") return "spark-up";
  if (t === "down") return "spark-down";
  return "spark-neutral";
});

// Legacy categorical trend. Suppressed when `delta` is set — the
// delta carries its own (numeric) arrow and showing two would be
// noisy.
const legacyTrend = computed(() =>
  resolvedDelta.value ? null : (props.opts?.trend ?? null),
);

const trendChar = computed(() => {
  switch (legacyTrend.value) {
    case "up":
      return "▲";
    case "down":
      return "▼";
    case "flat":
      return "▬";
    default:
      return null;
  }
});

const trendClass = computed(() => {
  switch (legacyTrend.value) {
    case "up":
      return "trend-up";
    case "down":
      return "trend-down";
    case "flat":
      return "trend-flat";
    default:
      return "";
  }
});

// Arrow + colour class for the delta block. Mirrors the legacy
// trend pill but sits next to the delta number, not the value.
const deltaArrow = computed(() => {
  switch (resolvedDelta.value?.trend) {
    case "up":
      return "▲";
    case "down":
      return "▼";
    case "flat":
      return "▬";
    default:
      return null;
  }
});

const deltaClass = computed(() => {
  switch (resolvedDelta.value?.trend) {
    case "up":
      return "delta-up";
    case "down":
      return "delta-down";
    case "flat":
      return "delta-flat";
    default:
      return "";
  }
});

function onClick() {
  if (props.opts?.id) emit("click-target", props.opts.id);
}
</script>

<template>
  <div
    ref="kpiEl"
    class="kpi"
    :class="{
      clickable: !!opts?.id && !loading && !errored,
      'is-loading': loading,
      'is-errored': errored,
      'is-empty': isEmpty && !!emptyText,
      'kpi--primary': !!opts?.primary,
      'kpi--print': isPrint(),
    }"
    :data-description="opts?.description ?? undefined"
    @click="loading || errored ? undefined : onClick()"
  >
    <div class="label">
      <LucentIcon
        v-if="opts?.icon && (opts.iconPosition ?? 'leading') === 'leading'"
        :name="opts.icon"
        :size="14"
        class="kpi-icon kpi-icon--leading"
      />
      <span>{{ label }}</span>
      <!--
        Info toggletip is hidden in print mode — it isn't reachable on
        paper, and the icon prints as dead pixels. The description is
        still attached to the tile via `data-description` so the PDF
        renderer's future footnote-scrape pass can collect it and emit a
        superscript-keyed reference.
      -->
      <InfoTip
        v-if="opts?.description && !isPrint()"
        :text="opts.description"
        :label="label"
      />
    </div>
    <div v-if="loading" class="kpi-spinner">
      <LucentSpinner :size="28" />
    </div>
    <div v-else-if="errored" class="kpi-placeholder kpi-placeholder--error">
      {{ errorText }}
    </div>
    <div
      v-else-if="isEmpty && emptyText"
      class="kpi-placeholder kpi-placeholder--empty"
    >
      {{ emptyText }}
    </div>
    <template v-else>
      <div ref="rowEl" class="value-row">
        <div ref="valueEl" class="value">
          <span :class="trendClass" v-if="trendChar">{{ trendChar }}</span>
          {{ display }}
        </div>
        <LucentIcon
          v-if="opts?.icon && opts.iconPosition === 'trailing'"
          ref="iconEl"
          :name="opts.icon"
          :size="20"
          class="kpi-icon kpi-icon--trailing"
        />
        <div v-if="resolvedDelta" ref="deltaEl" class="delta" :class="deltaClass">
          <span class="delta-arrow" v-if="deltaArrow">{{ deltaArrow }}</span>
          <span class="delta-value">{{ resolvedDelta.display }}</span>
          <span class="delta-caption" v-if="resolvedDelta.caption">{{
            resolvedDelta.caption
          }}</span>
        </div>
      </div>
      <svg
        v-if="sparkPath"
        class="spark"
        :class="sparkClass"
        :viewBox="`0 0 ${SPARK_W} ${SPARK_H}`"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <!-- `non-scaling-stroke`: the viewBox stretches to the tile
             (`preserveAspectRatio="none"`), which otherwise stretched
             the line's thickness with it. -->
        <path
          :d="sparkPath"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
          vector-effect="non-scaling-stroke"
        />
      </svg>
      <div class="caption" v-if="opts?.caption">{{ opts.caption }}</div>
    </template>
  </div>
</template>

<style scoped>
.kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Lets the delta below query the tile's own width. KPIs sit in
     stretched column cells / flex columns, never shrink-to-fit. */
  container-type: inline-size;
  background: var(--card);
  /* Themer-facing tokens: --lucent-card-border / --lucent-card-shadow /
     --lucent-card-pad. Padding falls back to the private
     `--_lucent-card-pad`, which EmbedHost narrows on small embeds. */
  border: var(--lucent-card-border, 1px solid var(--rule));
  border-radius: var(--radius);
  padding: var(--lucent-card-pad, var(--_lucent-card-pad, 16px 18px));
  box-shadow: var(--lucent-card-shadow, none);
  min-width: 0;
  transition:
    border-color 120ms ease,
    box-shadow 120ms ease,
    transform 120ms ease;
}
.kpi.clickable {
  cursor: pointer;
}
.kpi.clickable:hover {
  border-color: var(--primary);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 18px rgba(10, 14, 28, 0.06);
  transform: translateY(-1px);
}
.kpi.clickable:active {
  transform: translateY(0);
}
.kpi.is-loading,
.kpi.is-empty,
.kpi.is-errored {
  /* Reserve roughly the same vertical space the value would have taken so
   * the page doesn't reflow when content swaps in. */
  min-height: 86px;
}
.kpi-placeholder {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  padding: 6px 0 2px;
  font-size: 13px;
  line-height: 1.35;
  color: var(--color-text-muted);
}
.kpi-placeholder--error {
  color: var(--color-negative);
}
.kpi-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 8px 0;
}
.label {
  color: var(--color-text-muted);
  /* Themer-facing: --lucent-font-label / --lucent-tracking-label /
     --lucent-case-label. Defaults preserve the stock register
     (uppercase 11px 600, 0.06em tracked). */
  font: var(--lucent-font-label, 600 11px/1.2 system-ui, sans-serif);
  letter-spacing: var(--lucent-tracking-label, 0.06em);
  text-transform: var(--lucent-case-label, uppercase);
  order: -1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.label :deep(.info-tip) {
  color: var(--color-text-muted);
}
/* Value sits flush-left with the delta block aligned to its baseline,
 * so "1,341  ▲ +12 vs last quarter" reads as a single line. The row
 * wraps if the tile is narrow. */
.value-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}
.value {
  /* Themer-facing: --lucent-font-value / --lucent-tracking-value.
   * Default keeps the existing `calc(30px * --lucent-font-scale)`
   * sizing so fullscreen / projection mode still scales the value.
   * `fit()` reads the post-calc computed font-size as its measurement
   * base, so the auto-fit pass works against whatever the theme
   * resolves the var to. */
  font: var(--lucent-font-value, 600 calc(30px * var(--lucent-font-scale, 1))/1.1 system-ui, sans-serif);
  color: var(--color-text);
  letter-spacing: var(--lucent-tracking-value, -0.01em);
  display: flex;
  align-items: baseline;
}
.caption {
  color: var(--color-text-muted);
  font-size: calc(12px * var(--lucent-font-scale, 1));
  line-height: 1.4;
}
.trend-up,
.trend-down,
.trend-flat {
  font-size: 0.6em;
  margin-right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 5px;
  border-radius: 4px;
  line-height: 1;
}
.trend-up {
  color: var(--color-positive);
  background: rgba(45, 122, 0, 0.1);
}
.trend-down {
  color: var(--color-negative);
  background: rgba(188, 18, 0, 0.1);
}
.trend-flat {
  color: var(--color-text-muted);
  background: var(--rule-soft);
}

/* Integrated delta block: arrow + signed number + (optional) caption,
 * sitting next to the value at the same baseline. The arrow + number
 * pair is one chip; the caption is plain muted text after it so it
 * reads "▲ +12 vs last quarter" without the caption sharing the chip. */
.delta {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: calc(13px * var(--lucent-font-scale, 1));
  line-height: 1.2;
}
/* Narrow tile: the delta takes its own line under the value instead of
   squeezing it. Declarative so it can't oscillate with the auto-fit. */
@container (max-width: 260px) {
  .delta {
    flex-basis: 100%;
  }
}
.delta-arrow {
  font-size: 0.85em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 5px;
  border-radius: 4px;
  line-height: 1;
}
.delta-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.delta-caption {
  color: var(--color-text-muted);
  font-size: 0.92em;
}
.delta-up .delta-arrow,
.delta-up .delta-value {
  color: var(--color-positive);
}
.delta-up .delta-arrow {
  background: rgba(45, 122, 0, 0.1);
}
.delta-down .delta-arrow,
.delta-down .delta-value {
  color: var(--color-negative);
}
.delta-down .delta-arrow {
  background: rgba(188, 18, 0, 0.1);
}
.delta-flat .delta-arrow,
.delta-flat .delta-value {
  color: var(--color-text-muted);
}
.delta-flat .delta-arrow {
  background: var(--rule-soft);
}

/* Inline sparkline. Sized via CSS — the SVG viewBox is unitless so the
 * line stretches to whatever width the tile allows. Height is fixed to
 * keep the row rhythm consistent across KPIs in a `sbi.columns(N, ...)`. */
.spark {
  width: 100%;
  height: 28px;
  display: block;
  margin-top: 2px;
}
.spark-up {
  color: var(--color-positive);
}
.spark-down {
  color: var(--color-negative);
}
.spark-neutral {
  color: var(--color-text-muted);
}

/* Hero treatment for the insight's headline KPI. Bigger value, accent rule
 * down the left, and a slightly more emphatic label. The rest of the KPI
 * layout (label-on-top via order:-1, trend pills) is preserved. */
.kpi.kpi--primary {
  /* Same public `--lucent-card-pad` as regular tiles so themes that go
   * dense (or spacious) apply uniformly. Unset, primary keeps its roomier
   * default via its own private token, which EmbedHost narrows to the
   * regular tile's on small embeds. Primary still reads as the headline
   * via the bigger label/value sizes below and the accent left-rule. */
  padding: var(--lucent-card-pad, var(--_lucent-card-pad-primary, 22px 24px 22px 22px));
  border-left: 3px solid var(--primary);
  gap: 6px;
}
.kpi.kpi--primary .label {
  color: var(--primary-deep);
  font-size: 12px;
}
.kpi.kpi--primary .value {
  /* See `--lucent-font-scale` note on `.value` above. Primary tiles
   * inherit the same scaling treatment with a larger base size. */
  font-size: calc(56px * var(--lucent-font-scale, 1));
  letter-spacing: -0.02em;
}
.kpi.kpi--primary .caption {
  font-size: calc(13px * var(--lucent-font-scale, 1));
}
.kpi.kpi--primary .delta {
  font-size: calc(15px * var(--lucent-font-scale, 1));
}
.kpi.kpi--primary .spark {
  height: 36px;
}
.kpi.kpi--primary .trend-up,
.kpi.kpi--primary .trend-down,
.kpi.kpi--primary .trend-flat {
  font-size: 0.5em;
  padding: 3px 6px;
}
.kpi.kpi--primary.is-loading {
  min-height: 124px;
}
/* Optional decorative icon — leading sits next to the label, trailing
   sits next to the value. Sized to the surrounding text so it reads as
   an integral part of the line, not a separate element. */
.kpi-icon {
  flex: 0 0 auto;
  color: currentColor;
  opacity: 0.7;
}
.kpi-icon--leading {
  /* Inherits the label's small font size; nudge baseline-alignment. */
  vertical-align: middle;
  margin-right: 6px;
  display: inline-flex;
}
.kpi-icon--trailing {
  align-self: center;
  margin-left: 8px;
  color: var(--card-muted);
}
.kpi.kpi--primary .kpi-icon--leading {
  margin-right: 8px;
}
.kpi.kpi--primary .kpi-icon--trailing {
  margin-left: 12px;
}
</style>
